import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { AppealResolutionSchema } from '../appeal-resolution/appeal-resolution';
import { ViolationActionSchema } from '../violation-action/violation-action';
import { ViolationStatusSchema } from '../violation-status/violation-status';
import { ViolationTypeKeySchema } from '../violation-type/violation-type';

/**
 * What the **offender** is told — the member's own side of an enforcement.
 *
 * The counterpart to `ViolationDetail`, and it withholds three things
 * that shape belongs to staff for: the AI's confidence, the AI's rationale, and
 * every moderator note. A member learning they were suspended "at 0.91
 * confidence" learns how to phrase the next message under the threshold, and a
 * reviewer's private note is written on the understanding that it is private.
 * What the member gets instead is the type's own translated `description`, which
 * is authored for exactly this reading.
 *
 * It also **never names the recipient** of the message, and carries no
 * conversation id: an enforcement notice is between the platform and the person
 * it acted on. Whether the other member reported them is not disclosed here.
 *
 * `messageBody` is the offender's **own words**, echoed back so the notice is
 * about something they can recognise rather than an abstract accusation. It is
 * their own content, so nothing is disclosed by returning it.
 */
export const MemberViolationNoticeSchema = z
  .object({
    id: UuidSchema,
    violationTypeKey: ViolationTypeKeySchema,
    /** The type's translated name and explanation, resolved to the member's locale. */
    title: z.string(),
    explanation: z.string(),
    /**
     * What was actually **done** — and `null` when nothing was.
     *
     * Nullable because a `dismissed` row is member-visible and has no action on
     * it: `dismiss()` never enforces, so `action_taken` stays null. A
     * non-nullable field forced a fallback, the fallback was `'warn'`, and the
     * result was a notice telling a member they had been warned by the very row
     * that records a moderator deciding not to act. "Nothing was done" is an
     * answer this shape has to be able to give.
     */
    action: ViolationActionSchema.nullable(),
    status: ViolationStatusSchema,
    occurredAt: z.iso.datetime(),
    /** The member's own message. Null once it has been purged with a closed account. */
    messageBody: z.string().nullable(),
    /** When a suspension ends — the answer to "until when", which P11c left open. */
    suspensionUntil: z.iso.datetime().nullable(),
    /**
     * Whether an appeal can still be filed.
     *
     * Derived server-side from `isAppealableViolationStatus` and whether one was
     * already filed, so the client never re-implements the policy and a disabled
     * button and a 422 can never disagree.
     */
    canAppeal: z.boolean(),
    appealedAt: z.iso.datetime().nullable(),
    appealResolution: AppealResolutionSchema.nullable(),
    /**
     * Whether this notice carries the two standing notices N20 requires.
     *
     * Flags rather than copy: the strings are rendered by the client from its
     * own catalogue (U8) and delivered as notification templates (N17), so the
     * api never ships a sentence. The no-refund notice additionally requires a
     * `ban`, because that is the only action that ends an entitlement the member
     * paid for.
     *
     * **Both ride on a consequence that STANDS** (`countsAgainstMember`), not on
     * the mere existence of a row. A `dismissed` detection is a moderator
     * looking and finding nothing, and an `overturned` one is the platform
     * agreeing it was wrong — telling either member that legal consequences may
     * follow is an accusation the platform explicitly declined to make. The
     * overturned case is the subtle one: reversing an appeal does not clear
     * `action_taken`, so a reinstated member was still being told their ban
     * would not be refunded.
     */
    showsLegalConsequencesNotice: z.boolean(),
    showsNoRefundNotice: z.boolean(),
  })
  .strict();

export type MemberViolationNotice = z.infer<typeof MemberViolationNoticeSchema>;
